import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { MagneticButton } from "@/components/magnetic-button";
import { SiteFooter } from "@/components/site-footer";
import { EASY_LOVER_CHROME_WEB_STORE_URL } from "@/lib/easy-lover";
import compactArtwork from "@/assets/easy-lover/easy-lover-compact-artwork.png.asset.json";
import icon from "@/assets/easy-lover/easy-lover-icon.png.asset.json";
import productScreenshot from "@/assets/easy-lover/easy-lover-in-lovable.png.asset.json";
import shareArtwork from "@/assets/easy-lover/easy-lover-share.jpg.asset.json";
import wideArtwork from "@/assets/easy-lover/easy-lover-wide-artwork.png.asset.json";

const TITLE = "Easy Lover: Customize Your Lovable Toolbar";
const DESCRIPTION =
  "Pin and reorder pages in Lovable's toolbar, keep GitHub settings beside Share, and save your layout locally with the Easy Lover Chrome extension.";
const CANONICAL = "https://vibing.inc/easy-lover";
const OG_IMAGE = `https://vibing.inc${shareArtwork.url}`;

const FAQS = [
  {
    question: "Does this work across my projects?",
    answer:
      "Yes. Your layout applies across Lovable projects in the same Chrome profile. Settings are stored locally and do not sync to other devices.",
  },
  {
    question: "Can I still open pages I have not pinned?",
    answer:
      "Yes. Open More to find every available page. Pinned pages stay in the list too, so the full editor navigation remains in one place.",
  },
  {
    question: "Does the GitHub button connect my account?",
    answer:
      "No. The optional shortcut opens Lovable's GitHub connector settings. Connecting an account and changing sync settings remain explicit actions in Lovable's own controls.",
  },
  {
    question: "Is this made by Lovable?",
    answer: "No. Easy Lover is an independent extension and is not affiliated with Lovable.",
  },
];

export const Route = createFileRoute("/easy-lover/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Easy Lover toolbar artwork" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", type: "text/markdown", href: "https://vibing.inc/md/easy-lover" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Easy Lover",
          description: DESCRIPTION,
          url: CANONICAL,
          image: OG_IMAGE,
          applicationCategory: "BrowserApplication",
          operatingSystem: "Chrome",
          ...(EASY_LOVER_CHROME_WEB_STORE_URL
            ? { downloadUrl: EASY_LOVER_CHROME_WEB_STORE_URL }
            : {}),
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          isAccessibleForFree: true,
          privacyPolicy: "https://vibing.inc/easy-lover/privacy",
        }),
      },
    ],
  }),
  component: EasyLoverPage,
});

function StoreAction() {
  if (EASY_LOVER_CHROME_WEB_STORE_URL) {
    return (
      <MagneticButton
        href={EASY_LOVER_CHROME_WEB_STORE_URL}
        target="_blank"
        rel="noreferrer noopener"
        variant="dark"
      >
        Add to Chrome <span aria-hidden>↗</span>
      </MagneticButton>
    );
  }

  return (
    <div>
      <Button disabled className="h-11 px-5">Coming soon</Button>
      <p id="store-review-status" className="mt-3 text-sm text-muted-foreground">
        Chrome Web Store review in progress.
      </p>
    </div>
  );
}

function EasyLoverPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section className="mx-auto max-w-[1400px] px-6 pb-16 pt-[clamp(9rem,18vh,13rem)] md:px-10 md:pb-24">
          <div className="flex items-center gap-4">
            <img
              src={icon.url}
              alt=""
              width={128}
              height={128}
              className="h-14 w-14 rounded-lg"
              decoding="async"
            />
            <p className="text-sm font-medium">Easy Lover for Chrome</p>
          </div>
          <h1 className="fluid-display mt-10 max-w-[15ch] font-display">
            Customize your Lovable toolbar with Easy Lover
          </h1>
          <p className="mt-8 max-w-[69ch] text-lg leading-relaxed text-muted-foreground md:text-2xl">
            Easy Lover is a Chrome extension that lets you choose which pages appear in your
            Lovable project toolbar. Pin the tools you use, arrange them in your preferred order,
            and open GitHub connector settings from a button beside Share.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <StoreAction />
            <MagneticButton href="#how-it-works" variant="outline-dark">
              See how it works <span aria-hidden>↓</span>
            </MagneticButton>
          </div>
        </section>

        <section aria-label="Easy Lover product artwork" className="mx-auto max-w-[1400px] px-4 pb-20 md:px-10 md:pb-32">
          <picture>
            <source media="(max-width: 767px)" srcSet={compactArtwork.url} />
            <img
              src={wideArtwork.url}
              alt="Easy Lover artwork showing a customizable Lovable toolbar"
              width={1983}
              height={793}
              fetchPriority="high"
              decoding="async"
              className="h-auto w-full rounded-lg"
            />
          </picture>
        </section>

        <section aria-labelledby="product-view-heading" className="border-t border-border/60">
          <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
            <div className="grid gap-8 md:grid-cols-12 md:items-end">
              <div className="md:col-span-7">
                <p className="text-xs uppercase text-muted-foreground">Inside Lovable</p>
                <h2 id="product-view-heading" className="fluid-h2 mt-4 max-w-[16ch] font-display">
                  Choose which pages appear in your toolbar.
                </h2>
              </div>
              <p className="max-w-[50ch] text-base leading-relaxed text-muted-foreground md:col-span-5 md:text-lg">
                Open More to see every page in one dropdown. Pin Preview, Files, Analytics,
                Security, Settings, or any other page you want close at hand. Unpinned pages remain
                available in More.
              </p>
            </div>
            <figure className="mt-12">
              <a
                href={productScreenshot.url}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Open the full-size Easy Lover screenshot in a new tab"
                className="group block overflow-hidden rounded-lg border border-border bg-secondary/40"
              >
                <img
                  src={productScreenshot.url}
                  alt="Easy Lover running in Lovable, with the More menu open, pin controls beside project pages, customized toolbar shortcuts, and a GitHub shortcut beside Share"
                  width={2452}
                  height={1270}
                  loading="lazy"
                  decoding="async"
                  className="h-auto w-full transition-transform duration-500 group-hover:scale-[1.01]"
                />
              </a>
              <figcaption className="mt-4 flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
                <span>The extension working in a Lovable project.</span>
                <a href={productScreenshot.url} target="_blank" rel="noreferrer noopener" className="underline underline-offset-4">
                  View full screenshot <span aria-hidden>↗</span>
                </a>
              </figcaption>
            </figure>
          </div>
        </section>

        <section id="how-it-works" aria-labelledby="how-heading" className="scroll-mt-24 border-t border-border/60 bg-secondary/40">
          <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:grid-cols-12 md:px-10 md:py-32">
            <div className="md:col-span-4">
              <p className="text-xs uppercase text-muted-foreground">How it works</p>
              <h2 id="how-heading" className="fluid-h2 mt-4 max-w-[12ch] font-display">
                How to customize your Lovable toolbar.
              </h2>
            </div>
            <ol className="border-t border-foreground/20 md:col-span-7 md:col-start-6">
              <li className="grid gap-4 border-b border-foreground/20 py-7 sm:grid-cols-[5rem_1fr]">
                <span className="font-mono text-sm text-muted-foreground">01</span>
                <div>
                  <h3 className="text-xl font-medium">Pin the pages you use</h3>
                  <p className="mt-2 max-w-[54ch] leading-relaxed text-muted-foreground">
                    Open More and click the pin beside a page to add or remove its toolbar shortcut.
                  </p>
                </div>
              </li>
              <li className="grid gap-4 border-b border-foreground/20 py-7 sm:grid-cols-[5rem_1fr]">
                <span className="font-mono text-sm text-muted-foreground">02</span>
                <div>
                  <h3 className="text-xl font-medium">Put them in your order</h3>
                  <p className="mt-2 max-w-[54ch] leading-relaxed text-muted-foreground">
                    Drag rows inside More, or use Alt+Up and Alt+Down from the keyboard. The toolbar
                    follows the same order.
                  </p>
                </div>
              </li>
              <li className="grid gap-4 border-b border-foreground/20 py-7 sm:grid-cols-[5rem_1fr]">
                <span className="font-mono text-sm text-muted-foreground">03</span>
                <div>
                  <h3 className="text-xl font-medium">Choose how labels appear</h3>
                  <p className="mt-2 max-w-[54ch] leading-relaxed text-muted-foreground">
                    Use the popup to show every label, only the active label, or icons alone. You
                    can also hide the GitHub shortcut, restore defaults, or disable Easy Lover.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section aria-labelledby="details-heading" className="border-t border-border/60">
          <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
            <h2 id="details-heading" className="fluid-h2 max-w-[17ch] font-display">
              Designed to feel at home in Lovable.
            </h2>
            <div className="mt-12 grid gap-10 border-t border-border pt-10 md:grid-cols-3">
              <div>
                <h3 className="text-xl font-medium">A familiar interface</h3>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  The toolbar follows Lovable's typography, colors, icons, and tab transitions.
                  Pinning animations also follow your system reduced-motion preference.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium">A direct GitHub shortcut</h3>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  The optional button beside Share opens Lovable's GitHub connector settings. It
                  never authorizes an account or changes synchronization on its own.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium">Saved in your Chrome profile</h3>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  Changes save automatically and apply across your Lovable projects in the same
                  Chrome profile. They stay local and do not sync across devices.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="privacy-support-heading" className="border-t border-border/60 bg-foreground text-background">
          <div className="on-ink mx-auto grid max-w-[1400px] gap-10 px-6 py-20 md:grid-cols-12 md:px-10 md:py-28">
            <div className="md:col-span-7">
              <h2 id="privacy-support-heading" className="fluid-h2 max-w-[15ch] font-display">
                Your toolbar preferences stay in Chrome.
              </h2>
              <p className="mt-6 max-w-[58ch] text-lg leading-relaxed text-background/70">
                Easy Lover has no telemetry or server of its own. Read exactly what the extension
                handles and stores in its dedicated privacy page.
              </p>
            </div>
            <div className="flex flex-col items-start gap-4 md:col-span-4 md:col-start-9 md:justify-end">
              <Link to="/easy-lover/privacy" className="text-lg underline underline-offset-4">
                Easy Lover privacy <span aria-hidden>→</span>
              </Link>
              <Link to="/contact" className="text-lg underline underline-offset-4">
                Contact support <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </section>

        <section aria-labelledby="faq-heading" className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
          <h2 id="faq-heading" className="fluid-h2 max-w-[16ch] font-display">Easy Lover questions.</h2>
          <dl className="mt-12 border-t border-border">
            {FAQS.map((item) => (
              <div key={item.question} className="grid gap-5 border-b border-border py-8 md:grid-cols-12">
                <dt className="text-xl font-medium md:col-span-5">{item.question}</dt>
                <dd className="max-w-[62ch] leading-relaxed text-muted-foreground md:col-span-6 md:col-start-7">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="availability-heading" className="border-t border-border/60">
          <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-28">
            <h2 id="availability-heading" className="fluid-h2 max-w-[16ch] font-display">
              {EASY_LOVER_CHROME_WEB_STORE_URL
                ? "Install Easy Lover from the Chrome Web Store."
                : "Easy Lover is coming soon."}
            </h2>
            <p className="mt-6 max-w-[55ch] text-lg leading-relaxed text-muted-foreground">
              {EASY_LOVER_CHROME_WEB_STORE_URL
                ? "Add the extension to Chrome, then open a Lovable project to customize your toolbar."
                : "Chrome Web Store review is in progress. The install link will appear here once the listing is verified."}
            </p>
            <div className="mt-8"><StoreAction /></div>
            <p className="mt-10 text-sm text-muted-foreground">
              Easy Lover is an independent extension and is not affiliated with Lovable.
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
